import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Game, Round } from '@/redux/reducers/gameReducer'
import { convertToDateTime } from '@/utils/date-time-utils'
import { DATE_TIME_FORMAT_HUMAN } from '@/utils/date-time-utils'
import { isYeekeeType, isEncryptLotto } from '@/utils/lotto-type-v2'
import { trans } from '@/i18n/utils'
import { i18n } from 'i18next'

export const useGameUtils = () => {
  const { i18n } = useTranslation()

  const getDetailName = useCallback(
    (game?: Game, round?: Round) => {
      const gameDate = round?.close_at
        ? convertToDateTime(
            round.close_at,
            DATE_TIME_FORMAT_HUMAN,
            i18n.language,
          )
        : '-'
      if (!game || !round) {
        return '-'
      }
      const showRound = isYeekeeType(game?.type) || isEncryptLotto(game?.type)
      if (showRound) {
        return `#${round?.round_number}`
      }
      return `${gameDate}`
    },
    [i18n.language],
  )

  const getGameName = useCallback(
    (game?: Game) => getGameTitle(i18n, game),
    [i18n],
  )

  return { getDetailName, getGameName }
}

/**
 * Generates a formatted game title with round information if available
 * @param i18n - The i18n instance for translations
 * @param game - The game object containing translations
 * @param round - Optional round object containing round number
 * @returns Formatted string with game name and round number, or just game name if no round is provided
 */
export function getGameWithRoundTitle(i18n: i18n, game?: Game, round?: Round) {
  if (!game) {
    return '-'
  }

  const isYeekee = isYeekeeType(game?.type)
  const isEncrypt = isEncryptLotto(game?.type)
  if (round && (isYeekee || isEncrypt)) {
    return i18n.t('lottoPage.lottoRound.round_name', {
      name: trans(i18n, game.translations, 'name'),
      round: round.round_number,
    })
  }

  return getGameTitle(i18n, game)
}

/**
 * Retrieves the translated game title
 * @param i18n - The i18n instance for translations
 * @param game - The game object containing translations
 * @returns The translated game name or '-' if game is not provided
 */
export function getGameTitle(i18n: i18n, game?: Game): string {
  if (!game) {
    return '-'
  }

  return trans(i18n, game.translations, 'name')
}
